import React, {ChangeEvent, useMemo} from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  ListSubheader,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import {Link as LinkIcon, StarRate as StarRateIcon} from "@material-ui/icons";
import {Link} from "react-router";
import {
  ContextId,
  WorkflowContext,
  WorkflowId,
  WorkflowInfo,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";

interface WorkflowSelectorProps {
  organisationId: OrganisationId;
  workflows: WorkflowInfo[];
  contextTypeIds?: ContextId[];
  contexts?: WorkflowContext[];
  selectedWorkflowId: WorkflowId;
  updateselectedWorkflowId: (workflowId: WorkflowId) => void;
  label?: string;
  showStarredWorkflows?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}

const WorkflowSelector = ({
  organisationId,
  workflows,
  contextTypeIds,
  contexts,
  selectedWorkflowId,
  updateselectedWorkflowId,
  label = "Workflow",
  showStarredWorkflows = false,
  disabled = false,
  fullWidth = false,
}: WorkflowSelectorProps) => {
  const handleChange = (event: ChangeEvent<{value: unknown}>) => {
    updateselectedWorkflowId(event.target.value as WorkflowId);
  };

  const filteredWorkflows = useMemo(
    () =>
      contextTypeIds && contextTypeIds.length > 0
        ? workflows?.filter(workflow =>
            contextTypeIds.includes(workflow.context_id),
          )
        : workflows,
    [workflows, contextTypeIds],
  );

  const [starredWorkflows, nonStarredWorkflows] = useMemo(() => {
    return (filteredWorkflows || []).reduce(
      (partitionedWorkflows, workflow) => {
        if (workflow.is_star) {
          partitionedWorkflows[0].push(workflow);
        } else {
          partitionedWorkflows[1].push(workflow);
        }
        return partitionedWorkflows;
      },
      [[], []] as [WorkflowInfo[], WorkflowInfo[]],
    );
  }, [filteredWorkflows]);

  const renderWorkflowMenuItem = (
    workflow: WorkflowInfo,
    isStarred = false,
  ) => (
    <MenuItem key={workflow.id} value={workflow.id}>
      {isStarred && (
        <StarRateIcon style={{marginRight: "8px", color: "#fbc02d"}} />
      )}
      {workflow.name}
    </MenuItem>
  );

  const workflowItems = useMemo(
    () =>
      filteredWorkflows && filteredWorkflows.length > 0
        ? [
            ...(showStarredWorkflows
              ? starredWorkflows.map(workflow =>
                  renderWorkflowMenuItem(workflow, true),
                )
              : []),
            ...(showStarredWorkflows &&
            starredWorkflows.length > 0 &&
            nonStarredWorkflows.length > 0
              ? [<Divider key="divider" style={{margin: "8px 0"}} />]
              : []),
            ...(showStarredWorkflows
              ? nonStarredWorkflows
              : filteredWorkflows
            ).map(workflow => renderWorkflowMenuItem(workflow)),
          ]
        : [
            <MenuItem key="no-workflows" disabled>
              No workflows available
            </MenuItem>,
          ],
    [
      filteredWorkflows,
      showStarredWorkflows,
      starredWorkflows,
      nonStarredWorkflows,
    ],
  );

  const groupedWorkflowItems = useMemo(() => {
    if (!contexts || !filteredWorkflows || filteredWorkflows.length === 0) {
      return null;
    }

    const workflowsByContextId = filteredWorkflows.reduce((acc, workflow) => {
      if (!acc[workflow.context_id]) {
        acc[workflow.context_id] = [];
      }
      acc[workflow.context_id].push(workflow);
      return acc;
    }, {} as Record<ContextId, WorkflowInfo[]>);

    const items: React.ReactNode[] = [];

    contexts.forEach(context => {
      const contextWorkflows = workflowsByContextId[context.id];
      if (!contextWorkflows || contextWorkflows.length === 0) {
        return;
      }

      items.push(
        <ListSubheader
          key={`context-${context.id}`}
          disableSticky
          onClickCapture={e => e.stopPropagation()}
        >
          {context.name}
        </ListSubheader>,
      );

      if (showStarredWorkflows) {
        const [starred, nonStarred] = contextWorkflows.reduce(
          (acc, workflow) => {
            acc[workflow.is_star ? 0 : 1].push(workflow);
            return acc;
          },
          [[], []] as [WorkflowInfo[], WorkflowInfo[]],
        );
        starred.forEach(w => items.push(renderWorkflowMenuItem(w, true)));
        nonStarred.forEach(w => items.push(renderWorkflowMenuItem(w)));
      } else {
        contextWorkflows.forEach(w => items.push(renderWorkflowMenuItem(w)));
      }
    });

    return items.length > 0 ? items : null;
  }, [contexts, filteredWorkflows, showStarredWorkflows]);

  const renderValue = (selectedId: unknown) => {
    const id = selectedId as WorkflowId;
    const foundWorkflow = workflows.find(workflow => workflow.id === id);
    return foundWorkflow ? foundWorkflow.name : "";
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        ...(fullWidth ? {width: "100%"} : {}),
      }}
    >
      <FormControl style={fullWidth ? {flex: 1, minWidth: 0} : {width: "30em"}}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={selectedWorkflowId === -1 ? "" : selectedWorkflowId}
          onChange={handleChange}
          renderValue={renderValue}
          disabled={workflows.length === 0 || disabled}
        >
          {groupedWorkflowItems ?? workflowItems}
        </Select>
      </FormControl>
      {selectedWorkflowId > 0 && (
        <Link
          to={`/organisation/${organisationId}/workflow/${selectedWorkflowId}/detail`}
        >
          <IconButton size="small" style={{padding: 0}}>
            <LinkIcon />
          </IconButton>
        </Link>
      )}
    </div>
  );
};

export default WorkflowSelector;
